import { z } from "zod";
import { CountryCode } from "../common/country.js";
import { CurrencyCode, Money } from "../common/money.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
/** Trade settings operators keep (ADM-4, card 050): the RuleSet, feature flags, and corridors. */
const Ops = { surface: "operator", auth: "operator", permission: "ops:config.manage" };
const Days = (max) => z.number().int().min(1).max(max);
/**
 * The places Civentia does not serve, and the markets not open yet (ADR 0025, card 127). Not served: the countries
 * and regions of the prohibited listings policy (5.1), refused at registration, in addresses, and in trade. Not open
 * yet: the EU, the EEA, and the UK until their representatives are appointed, refused at registration.
 */
export const DEFAULT_COUNTRY_RULES = {
    notServed: ["CU", "IR", "KP", "SY", "RU", "BY"],
    regionsNotServed: ["UA-43", "UA-40", "UA-14", "UA-09", "UA-65", "UA-23"],
    notOpen: [
        "AT",
        "BE",
        "BG",
        "HR",
        "CY",
        "CZ",
        "DK",
        "EE",
        "FI",
        "FR",
        "DE",
        "GR",
        "HU",
        "IE",
        "IT",
        "LV",
        "LT",
        "LU",
        "MT",
        "NL",
        "PL",
        "PT",
        "RO",
        "SK",
        "SI",
        "ES",
        "SE",
        "IS",
        "LI",
        "NO",
        "GB",
    ],
};
export const CountryRules = z
    .object({
    notServed: z
        .array(CountryCode)
        .describe("Countries not served: refused at registration, in addresses, and in trade"),
    regionsNotServed: z
        .array(z.string().regex(/^[A-Z]{2}-[A-Z0-9]{1,3}$/, "An ISO 3166-2 code"))
        .describe("Regions not served, by ISO 3166-2 code, such as UA-43 for Crimea"),
    notOpen: z.array(CountryCode).describe("Markets not open yet: refused at registration"),
})
    .meta({ id: "CountryRules" });
/**
 * A RuleSet's values (`docs/06-db-schema.md` section 10, ADR 0007): the windows trade runs on, the platform fee, and the
 * places served. An order keeps the version in force when it was created.
 */
export const RuleSetValues = z
    .object({
    inquiryExpiryDays: Days(365).describe("An inquiry without a quote expires after this many days"),
    quoteValidityDays: z
        .object({ default: Days(365), max: Days(365) })
        .describe("A quote's validity when the seller sets none, and the longest allowed"),
    paymentWindowDays: Days(90).describe("Days to pay an item once it is due; an unpaid first item cancels the order"),
    confirmation: z.object({
        windowDays: Days(60).describe("Days after delivery to confirm receipt before it is confirmed automatically"),
        reminderDaysBeforeEnd: z.number().int().min(0).max(59),
    }),
    afterSales: z
        .object({
        windowDays: Days(365).describe("Days after delivery during which the buyer may open a case"),
        responseDays: Days(30).describe("Days for the side that must answer a case next"),
        evidenceDays: Days(30).describe("Days for each side's evidence once a case is escalated"),
        appealDays: Days(60).describe("Days after a dispute decision to appeal it"),
    })
        .describe("After-sales windows (OPS-2, card 087)"),
    gst: z
        .object({ rateBps: z.number().int().min(0).max(10_000) })
        .describe("Singapore's standard GST rate, which standard-rated lines of sellers' invoices carry (ADR 0007, card 093)"),
    fee: z.object({
        rateBps: z.number().int().min(0).max(10_000),
        minimum: Money.describe("Per order, in the RuleSet's currency"),
        cap: Money.describe("Per order, in the RuleSet's currency"),
    }),
    countries: CountryRules.default({
        notServed: [...DEFAULT_COUNTRY_RULES.notServed],
        regionsNotServed: [...DEFAULT_COUNTRY_RULES.regionsNotServed],
        notOpen: [...DEFAULT_COUNTRY_RULES.notOpen],
    }).describe("The places Civentia does not serve, and the markets not open yet; ADR 0025's lists by default"),
})
    .superRefine((values, context) => {
    if (values.quoteValidityDays.default > values.quoteValidityDays.max)
        context.addIssue({ code: "custom", path: ["quoteValidityDays", "default"], message: "ABOVE_MAX" });
    if (values.confirmation.reminderDaysBeforeEnd >= values.confirmation.windowDays)
        context.addIssue({
            code: "custom",
            path: ["confirmation", "reminderDaysBeforeEnd"],
            message: "OUT_OF_RANGE",
        });
    const { minimum, cap } = values.fee;
    if (minimum.currency !== cap.currency)
        context.addIssue({ code: "custom", path: ["fee", "cap", "currency"], message: "MIXED_CURRENCIES" });
    else if (BigInt(minimum.amountMinor) < 0n || BigInt(minimum.amountMinor) > BigInt(cap.amountMinor))
        context.addIssue({ code: "custom", path: ["fee", "minimum"], message: "ABOVE_MAX" });
})
    .meta({ id: "RuleSetValues" });
export const RuleSetView = z
    .object({
    version: z.number().int().positive(),
    values: RuleSetValues,
    effectiveFrom: Timestamp,
    createdBy: z.string().describe("The operator's id, or `seed`"),
    createdAt: Timestamp,
})
    .meta({ id: "RuleSet" });
/** The flags the platform knows; a value is a switch. */
/** `enterprise_plans` puts the plans' limits and fees in force after the pilot (ADR 0023); off until then. */
export const FEATURE_FLAGS = ["bank_transfer_fallback", "enterprise_plans"];
export const FeatureFlagKey = z.enum(FEATURE_FLAGS).meta({ id: "FeatureFlagKey" });
export const FeatureFlagView = z
    .object({
    key: FeatureFlagKey,
    value: z.boolean(),
    updatedBy: z.string(),
    updatedAt: Timestamp,
})
    .meta({ id: "FeatureFlag" });
export const CorridorView = z
    .object({
    currency: CurrencyCode,
    enabled: z.boolean().describe("Quotes and orders may use the currency"),
    paymentMethods: z.array(z.string()).describe("The buyer's payment rails through Airwallex, from stage 6"),
    payoutSupported: z.boolean(),
    notes: z.string().nullable(),
    updatedBy: z.string(),
    updatedAt: Timestamp,
})
    .meta({ id: "Corridor" });
export const TradeSettings = z
    .object({
    rulesetVersion: z.number().int().positive(),
    rules: RuleSetValues,
    currencies: z.array(CurrencyCode).describe("Currencies quotes and orders may use"),
    bankTransfer: z
        .boolean()
        .describe("Whether the interim bank transfer is offered on new orders (ADR 0005)"),
})
    .meta({ id: "TradeSettings" });
export const getTradeSettings = defineRoute({
    id: "operations.trade_settings.get",
    method: "GET",
    path: "/v1/trade/settings",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "The trade settings in force: the RuleSet's windows and fee, the enabled currencies, and whether the bank transfer is offered. A change reaches every process within a minute",
    prd: ["ADM-4", "PAY-11"],
    responses: { 200: { description: "Settings", schema: TradeSettings } },
    errors: ["AUTH_REQUIRED"],
});
export const listRuleSets = defineRoute({
    ...Ops,
    id: "ops.config.rulesets.list",
    method: "GET",
    path: "/v1/ops/rulesets",
    marks: [],
    summary: "Every RuleSet version, newest first, and the one in force",
    prd: ["ADM-4"],
    responses: {
        200: {
            description: "Versions",
            schema: z.object({ items: z.array(RuleSetView), currentVersion: z.number().int().positive() }),
        },
    },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED"],
});
export const createRuleSet = defineRoute({
    ...Ops,
    id: "ops.config.rulesets.create",
    method: "POST",
    path: "/v1/ops/rulesets",
    marks: ["step-up"],
    summary: "A new RuleSet version, in force from now or a later time; orders already created keep theirs. Never backdated",
    prd: ["ADM-4"],
    request: { body: z.object({ values: RuleSetValues, effectiveFrom: Timestamp.optional() }) },
    responses: { 201: { description: "The new version", schema: RuleSetView } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "STEP_UP_REQUIRED", "VALIDATION_FAILED"],
});
export const listFeatureFlags = defineRoute({
    ...Ops,
    id: "ops.config.flags.list",
    method: "GET",
    path: "/v1/ops/feature-flags",
    marks: [],
    summary: "The feature flags and their values",
    prd: ["ADM-4", "PAY-11"],
    responses: { 200: { description: "Flags", schema: z.object({ items: z.array(FeatureFlagView) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED"],
});
export const setFeatureFlag = defineRoute({
    ...Ops,
    id: "ops.config.flags.set",
    method: "PUT",
    path: "/v1/ops/feature-flags/{key}",
    marks: ["step-up"],
    summary: "Switch a flag, with a reason for the audit; every process follows within a minute",
    prd: ["ADM-4", "PAY-11"],
    request: {
        params: z.object({ key: FeatureFlagKey }),
        body: z.object({ value: z.boolean(), reason: z.string().trim().min(3).max(500) }),
    },
    responses: { 200: { description: "The flag", schema: FeatureFlagView } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "STEP_UP_REQUIRED", "VALIDATION_FAILED"],
});
export const listCorridors = defineRoute({
    ...Ops,
    id: "ops.config.corridors.list",
    method: "GET",
    path: "/v1/ops/corridors",
    marks: [],
    summary: "Every currency the platform knows, enabled or not (ADR 0006)",
    prd: ["ADM-4"],
    responses: { 200: { description: "Corridors", schema: z.object({ items: z.array(CorridorView) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED"],
});
export const updateCorridor = defineRoute({
    ...Ops,
    id: "ops.config.corridors.update",
    method: "PUT",
    path: "/v1/ops/corridors/{currency}",
    marks: ["step-up"],
    summary: "Enable or disable a currency for new quotes, and record its rails and payout support",
    prd: ["ADM-4"],
    request: {
        params: z.object({ currency: CurrencyCode }),
        body: z.object({
            enabled: z.boolean(),
            paymentMethods: z.array(z.string().regex(/^[a-z][a-z0-9_]{1,39}$/)).max(20),
            payoutSupported: z.boolean(),
            notes: z.string().trim().max(500).nullable(),
        }),
    },
    responses: { 200: { description: "The corridor", schema: CorridorView } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "STEP_UP_REQUIRED", "NOT_FOUND", "VALIDATION_FAILED"],
});
export const configRoutes = [
    getTradeSettings,
    listRuleSets,
    createRuleSet,
    listFeatureFlags,
    setFeatureFlag,
    listCorridors,
    updateCorridor,
];
//# sourceMappingURL=config.routes.js.map